import React, { useEffect, useState } from "react";
import SafeIcon from "../../../common/SafeIcon";
import * as FiIcons from "react-icons/fi";
import { clientBillService } from "../../../services/client/clientBillService";

const { FiX } = FiIcons;

function money(value, currency) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Not available";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amount);
  } catch {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(amount);
  }
}

function dateLabel(value) {
  if (!value) return "Not provided";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function Field({ label, value }) {
  return <div className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p><p className="mt-1 break-words text-sm font-semibold text-slate-800">{value || "Not provided"}</p></div>;
}

export default function ClientExpenseDetailsModal({ organizationId, expense: selectedExpense, onClose }) {
  const [expense, setExpense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!selectedExpense || !organizationId) {
      setExpense(null);
      return undefined;
    }
    let current = true;
    setExpense(null);
    setLoading(true);
    setError("");
    clientBillService
      .getClientExpenseDetails({ organizationId, expenseId: selectedExpense.id })
      .then((detail) => current && setExpense(detail))
      .catch((loadError) => current && setError(loadError?.message || "Unable to load expense details."))
      .finally(() => current && setLoading(false));
    return () => { current = false; };
  }, [organizationId, selectedExpense?.id]);

  useEffect(() => {
    if (!selectedExpense) return undefined;
    const closeOnEscape = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedExpense, onClose]);

  if (!selectedExpense) return null;
  const displayed = expense || selectedExpense;
  const currency = displayed.currency || "USD";

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section role="dialog" aria-modal="true" aria-labelledby="client-expense-title" className="max-h-[94dvh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:max-w-2xl sm:rounded-3xl">
        <header className="flex items-start justify-between border-b border-slate-100 px-5 py-4 sm:px-7">
          <div><p className="text-xs font-bold uppercase tracking-widest text-violet-700">Read-only ledger expense</p><h2 id="client-expense-title" className="mt-1 text-xl font-black text-slate-900">Expense details</h2></div>
          <button type="button" onClick={onClose} aria-label="Close expense details" className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"><SafeIcon icon={FiX} className="text-lg" /></button>
        </header>
        <div className="space-y-5 p-5 sm:p-7">
          {loading && <p className="text-sm text-slate-500">Loading expense details…</p>}
          {error && <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Date" value={dateLabel(displayed.date)} />
            <Field label="Amount (debit)" value={money(displayed.debit, currency)} />
            <Field label="Description" value={displayed.description} />
            <Field label="Vendor / Payee" value={displayed.vendorName || displayed.payee} />
            <Field label="Expense category" value={[displayed.account?.code, displayed.account?.name].filter(Boolean).join(" · ")} />
            <Field label="Source" value={displayed.source} />
            <Field label="Status" value={displayed.status} />
            <Field label="Batch / reference" value={displayed.batch_id || displayed.reference_id || displayed.id} />
            <Field label="Related source account" value={displayed.source_account?.name} />
            <Field label="Credit side" value={money(displayed.credit, currency)} />
          </div>
          {displayed.notes && <section className="rounded-xl border border-slate-200 p-4"><h3 className="text-xs font-bold uppercase text-slate-400">Notes</h3><p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{displayed.notes}</p></section>}
          <p className="text-xs text-slate-500">Expense records are read from posted ledger lines classified as an expense account. The original debit and credit values are shown for accounting context.</p>
        </div>
        <footer className="border-t border-slate-100 px-5 py-4 text-right sm:px-7"><button type="button" onClick={onClose} className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800">Close</button></footer>
      </section>
    </div>
  );
}